import React, { useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment-timezone";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Grid,
  Paper,
  CircularProgress,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers";
import { MainCTABtn } from "components/common/Elements/MainCTAButtons";

// --- Styled Containers ---
const Container = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;

  /* xs & sm */
  @media (max-width: 599px) {
    padding: 8px; /* reclaim screen space */
    max-width: 100%; /* true full width */
  }
`;

const BookingSection = styled(Paper)`
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding: 24px;
  border-radius: 20px;
  background: linear-gradient(145deg, #f8fbff, #e6f0ff);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);

  /* xs & sm */
  @media (max-width: 599px) {
    padding: 14px; /* tighter, cleaner */
    border-radius: 14px;
    gap: 20px;
  }
`;

const QuickChips = styled(Stack)`
  flex-direction: row;
  gap: 10px;
  padding: 12px 0;
  overflow-x: auto;
`;

const TimeSlotsContainer = styled(Box)`
  max-height: 280px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 14px;
  padding-top: 8px;

  @media (max-width: 599px) {
    grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
    gap: 10px;
    max-height: 500px; /* let it flow */
  }
`;

const Slot = styled(Box)`
  padding: 12px 10px;
  border-radius: 12px;
  text-align: center;
  font-weight: 600;
  font-size: 0.95rem;

  cursor: ${({ booked, disabled }) =>
    booked || disabled ? "not-allowed" : "pointer"};

  border: 2px solid
    ${({ booked, disabled, selected }) =>
      booked
        ? "#facc15" // reserved
        : disabled
        ? "#d1d5db" // past (grey border)
        : selected
        ? "#2563eb" // active
        : "#d1d5db"};

  background: ${({ booked, disabled, selected }) =>
    booked
      ? "#fff7d6" // reserved
      : disabled
      ? "#f3f4f6" // grey background for past
      : selected
      ? "linear-gradient(135deg, #022b9cbb, #276cffff)"
      : "#fff"};

  color: ${({ booked, disabled, selected }) =>
    booked
      ? "#92400e" // reserved
      : disabled
      ? "#9ca3af" // past (grey text)
      : selected
      ? "#fff"
      : "#111827"};

  transition: all 0.25s ease;

  &:hover {
    transform: ${({ booked, disabled }) =>
      booked || disabled ? "none" : "translateY(-3px)"};
    box-shadow: ${({ booked, disabled }) =>
      booked || disabled ? "none" : "0 6px 18px rgba(37, 99, 235, 0.25)"};
  }

  /* xs & sm */
  @media (max-width: 599px) {
    padding: 8px 6px;
    font-size: 0.85rem;
    border-radius: 10px;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

const FloatingBookButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #022b9cbb, #276cffff);
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
  &:hover {
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    transform: translateY(-2px);
  }
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const FloatingSummary = styled(Box)`
  position: fixed;
  bottom: 125px;
  right: 20px;
  padding: 10px 10px;
  background: white;
  color: black;
  border: none;
  border-radius: 16px;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);

  /* Hide on xs & sm */
  @media (max-width: 599px) {
    display: none;
  }
`;

// --- Main Component ---
const TimePickerModule = ({
  doctorData,
  onBook,
  userTimeZone,
  fetchDoctorDetailsByDate,
  dateLoading,
  onSlotSelect,
}) => {
  const [selectedDate, setSelectedDate] = useState(moment().tz(userTimeZone));
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [weekDates, setWeekDates] = useState([]);

  useEffect(() => {
    // Start of ISO week (Monday)
    const start = selectedDate.clone().startOf("isoWeek");
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(start.clone().add(i, "days"));
    }
    setWeekDates(dates);
  }, [selectedDate]);

  useEffect(() => {
    // Only proceed if the selection is complete and a callback is provided
    if (selectedSlot && selectedDate && onSlotSelect) {
      // 1. Get the combined moment object in the user's timezone
      // const slotDateTimeMoment = moment(selectedSlot.timeUtc).tz(userTimeZone);

      // Ensure the slot time string ends with 'Z' (to guarantee UTC parsing)
      const safeUtc = selectedSlot.timeUtc.endsWith("Z")
        ? selectedSlot.timeUtc
        : `${selectedSlot.timeUtc}Z`;

      const slotDateTimeMoment = moment.utc(safeUtc).tz(userTimeZone);

      // 2. Construct the data object
      const selectedDetails = {
        // A. UTC Time (Unambiguous time for backend)
        utcTime: selectedSlot.timeUtc,

        // B. Local Display Time/Date (User's timezone)
        localTimeDisplay: slotDateTimeMoment.format("hh:mm A"),
        localDateDisplay: slotDateTimeMoment.format("MMM D, YYYY"),
        localDateTimeMoment: slotDateTimeMoment, // Full moment object if needed

        // C. Slot Status/Details
        isReserved: selectedSlot.reserved || false,
        // Include other non-time-related slot data
        slotData: selectedSlot,
      };

      // 3. Send the clean data object to the parent
      onSlotSelect(selectedDetails);
    } else if (onSlotSelect) {
      // Clear selection (e.g., when the date changes or a slot is deselected)
      onSlotSelect(null);
    }

    // Dependencies: trigger this effect whenever a selection changes
  }, [selectedSlot, selectedDate, userTimeZone, onSlotSelect]);

  const handleDateChange = (date) => {
    if (!date) return;
    const d = moment(date).tz(userTimeZone);
    setSelectedDate(d);
    setSelectedSlot(null);
    fetchDoctorDetailsByDate(d.toISOString(), userTimeZone);
  };

  const minDate = moment().tz(userTimeZone);
  const maxDate = minDate.clone().add(90, "days");
  const now = moment().tz(userTimeZone); // Current time in user's timezone;
  const timeWithGap = now.clone().add(60, "minutes");

  const slotsForSelectedDate = selectedDate
    ? doctorData.nextAppointment[selectedDate.format("YYYY-MM-DD")] || []
    : [];

  return (
    <Container>
      <BookingSection>
        {/* Calendar + Quick Chips */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography fontWeight={600} mb={1} color="primary">
              Pick a Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                value={selectedDate}
                onChange={handleDateChange}
                minDate={minDate}
                maxDate={maxDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: { backgroundColor: "white", borderRadius: 2 },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography fontWeight={600} mb={1} color="primary">
              Quick Week
            </Typography>
            <QuickChips>
              {weekDates.map((d) => {
                const isPast = d.isBefore(now, "day");
                const isSelected = d.isSame(selectedDate, "day");

                return (
                  <Chip
                    key={d.format("YYYY-MM-DD")}
                    label={
                      <Stack spacing={0.3} alignItems="center">
                        <Typography variant="caption">
                          {d.format("ddd")}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {d.format("DD")}
                        </Typography>
                      </Stack>
                    }
                    color={isSelected ? "primary" : "default"}
                    variant={isSelected ? "filled" : "outlined"}
                    disabled={isPast}
                    onClick={() => {
                      if (!isPast) {
                        const normalized = d.clone().tz(userTimeZone);
                        setSelectedDate(normalized);
                        setSelectedSlot(null);
                        fetchDoctorDetailsByDate(
                          normalized.toISOString(),
                          userTimeZone
                        );
                      }
                    }}
                    sx={{
                      minWidth: 60,
                      borderRadius: "12px",
                      py: 1,
                      fontWeight: 500,
                      minHeight: 70,
                    }}
                  />
                );
              })}
            </QuickChips>
          </Grid>
        </Grid>

        {/* Time Slots */}
        <Box>
          <Typography fontWeight={700} mb={1}>
            Available Times{" "}
            {selectedDate && `- ${selectedDate.format("MMM D, YYYY")}`}
          </Typography>
          <TimeSlotsContainer>
            {dateLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : slotsForSelectedDate.length > 0 ? (
              slotsForSelectedDate
                .filter((slot) => {
                  const slotDateTime = moment(slot.timeUtc).tz(userTimeZone);
                  // Exclude past slots
                  // return !slotDateTime.isBefore(now);
                  return slotDateTime.isSameOrAfter(timeWithGap); // with one hour gap
                })

                .map((slot, i) => {
                  const slotDateTime = moment(slot.timeUtc).tz(userTimeZone);
                  // const isPast = slotDateTime.isBefore(now);
                  // const isPast = slotDateTime.isBefore(timeWithGap); // with one hour gap
                  // const isBooked = slot.reserved || isPast;
                  const isBooked = slot.reserved;

                  return (
                    <Slot
                      key={i}
                      booked={slot.reserved}
                      // disabled={isPast}
                      selected={selectedSlot?.timeUtc === slot.timeUtc}
                      onClick={() => !isBooked && setSelectedSlot(slot)}
                    >
                      {slotDateTime.format("hh:mm A")}
                    </Slot>
                  );
                })
            ) : (
              <Typography variant="body2" color="text.secondary">
                No slots available for this date.
              </Typography>
            )}
          </TimeSlotsContainer>
        </Box>
      </BookingSection>

      <MainCTABtn
        variant="contained"
        fullWidth
        disabled={!selectedDate || !selectedSlot}
        onClick={() => onBook(selectedSlot)}
        sx={{ mt: 2, height: 40 }}
      >
        Book Appointment
      </MainCTABtn>

      {/* Floating Book Button */}
      <FloatingSummary>
        <Grid container>
          {selectedDate && (
            <Grid item>
              Selected Date :
              <Typography style={{ fontSize: "0.90rem" }}>
                {selectedDate.format("MMM D, YYYY")}
              </Typography>
            </Grid>
          )}
          {selectedSlot && (
            <Grid item>
              Time :
              <Typography style={{ fontSize: "0.90rem" }}>
                {moment(selectedSlot?.timeUtc)
                  .tz(userTimeZone)
                  .format("hh:mm A")}
              </Typography>
            </Grid>
          )}
        </Grid>
        <Grid>
          <Typography style={{ fontSize: "0.90rem" }}>
            Consultation Fee : {doctorData?.displayingFee} LKR
          </Typography>
        </Grid>
        <FloatingBookButton
          disabled={!selectedDate || !selectedSlot}
          onClick={() =>
            // onBook(selectedDate.format("YYYY-MM-DD"), selectedSlot)
            onBook(selectedSlot)
          }
        >
          Book Appointment
        </FloatingBookButton>
      </FloatingSummary>
    </Container>
  );
};

export default TimePickerModule;
